import HowToRegIcon from '@mui/icons-material/HowToReg';
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Field, FieldGrid, SectionCard } from '../../../components/Section';
import { usePermissions } from '../../../hooks';
import { useApproveTreasuryEmployeeMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryCompanySetup } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../utils/treasury';

type TreasuryApproveEmployeeStepProps = {
  setup: TreasuryCompanySetup;
  onDone: () => void;
};

/**
 * Step 4 — approve the treasury employee: copies the onboarding KYC to the user, activates the employee and makes
 * the company's treasury setup ACTIVE (the page then moves to the company KYB).
 */
export const TreasuryApproveEmployeeStep = ({ setup, onDone }: TreasuryApproveEmployeeStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canApprove = usePermissions({ items: [permissions.Treasury.approveEmployee] });
  const [approveTreasuryEmployee, { isLoading: isApproving }] = useApproveTreasuryEmployeeMutation();

  const approve = async () => {
    if (!setup.employeeId) return;

    try {
      await approveTreasuryEmployee({ companyId: setup.companyId, employeeId: setup.employeeId }).unwrap();
      enqueueSnackbar('Treasury employee approved!', { variant: 'success' });
      onDone();
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Treasury employee approve error!'), { variant: 'error' });
    }
  };

  return (
    <SectionCard title="Treasury Employee" description="Check the owner's details, then approve.">
      <Stack spacing={3}>
        <FieldGrid>
          <Field label="Full Name (English)" value={setup.employee?.full_name_en} />
          <Field label="Full Name (Arabic)" value={setup.employee?.full_name_ar} />
          <Field label="Work Email" value={setup.employee?.email} />
          <Field label="National ID" value={setup.employee?.national_id} />
        </FieldGrid>
        <Typography variant="caption" color="text.secondary">
          Approving copies the onboarding KYC to the owner&apos;s account, activates the employee and makes the treasury
          active.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isApproving}
            startIcon={<HowToRegIcon />}
            onClick={approve}
            disabled={isApproving || !canApprove}
          >
            Approve Employee
          </LoadingButton>
        </Box>
      </Stack>
    </SectionCard>
  );
};
